import { useCallback, useEffect, useState } from 'react';
import { type TextSelectionRange } from '@meitu/saas-editor-core/es/layers/text-layer';
import { type TextEditorPlugin } from '@meitu/saas-editor-core/es/plugins/text-editor';
import { type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { useEditor } from './use-editor';

type TextRangeAttrsState = ReturnType<TextEditorPlugin['resolveRangeAttrsState']> | null;

export const useTextRangeAttrsState = (layerId: string): TextRangeAttrsState => {
    const editor = useEditor();

    const resolveRangeAttrsState = useCallback((): TextRangeAttrsState => {
        const textEditor = editor.getInstance<TextEditorPlugin>('TextEditorPlugin');
        if (!textEditor) return null;

        const session = textEditor.getSession();
        if (!session || session.editingLayer.id !== layerId) return null;

        const layerData = session.editingLayer.data as ITextLayerProps;
        if (!layerData.textContents?.length) return null;

        return textEditor.resolveRangeAttrsState();
    }, [editor, layerId]);

    const [attrsState, setAttrsState] = useState<TextRangeAttrsState>(resolveRangeAttrsState);

    useEffect(() => {
        const textEditor = editor.getInstance<TextEditorPlugin>('TextEditorPlugin');
        if (!textEditor) {
            setAttrsState(null);
            return;
        }

        const updateTextRangeAttrsState = () => setAttrsState(resolveRangeAttrsState());
        updateTextRangeAttrsState();

        return textEditor.onSessionChange(updateTextRangeAttrsState);
    }, [editor, resolveRangeAttrsState]);

    return attrsState;
};

export const useTextRange = (layerId: string): TextSelectionRange | null => {
    const editor = useEditor();

    const getTextRange = useCallback((): TextSelectionRange | null => {
        const textEditor = editor.getInstance<TextEditorPlugin>('TextEditorPlugin');
        if (!textEditor) return null;

        const session = textEditor.getSession();
        if (!session || session.editingLayer.id !== layerId) return null;

        return textEditor.getSelectionRange();
    }, [editor, layerId]);

    const [textRange, setTextRange] = useState<TextSelectionRange | null>(getTextRange);

    useEffect(() => {
        const textEditor = editor.getInstance<TextEditorPlugin>('TextEditorPlugin');
        if (!textEditor) {
            setTextRange(null);
            return;
        }

        const updateTextRange = () => setTextRange(getTextRange());
        updateTextRange();

        return textEditor.onSessionChange(updateTextRange);
    }, [editor, getTextRange]);

    return textRange;
};
